import { executeOnce, objectEntries, type ReadonlyRecord } from '../../../utils'
import {
    type AuthenticationRequest,
    type AuthenticationStatus,
    type AuthenticationWithBackupcode,
    type AuthenticationWithOtpRequest,
    Capability,
    ConnectwareError,
    ConnectwareErrorType,
    InvalidOtpError,
    MfaRequiredError,
    MfaSetupRequiredError,
    SessionFromTokenNotFoundError,
    SSOAuthenticationMethod,
    TokenExpiredError,
} from '../../../domain'
import type { AuthenticationResponse, AuthenticationService, SessionResponse } from '../../../application'

import type { BackendJsonRequestContent, BackendJsonResponseContent, BackendMethodPath, OrchestratorResponse } from '../../Connectware'
import type { SessionResponse as BackendSessionResponse, InvalidOTPResponse, LoginResponse, Permission, UserBannedResponse } from './Types'
import { type CapabilityCheckers, mapBackupCodeBodyRequest, mapLoginResponse, mapOtpBodyRequest, mapSessionResponse } from './mappers'
import { ConnectwareHTTPCapabilitiesService } from './Capabilities'

const createIgnorer =
    <T>(value: T): (() => T) =>
    () =>
        value
const ignoreOrchestratorError = createIgnorer(null)
const ignoreMfaError = createIgnorer(false)

export class ConnectwareHTTPAuthenticationService extends ConnectwareHTTPCapabilitiesService implements AuthenticationService {
    private readonly baseURL: string

    private readonly ssoUrlLoginPaths: ReadonlyRecord<SSOAuthenticationMethod, BackendMethodPath<'get'>> = {
        [SSOAuthenticationMethod.MICROSOFT_ENTRA_ID]: '/api/msEntraId/login',
    }

    private readonly ssoFetchers: ReadonlyRecord<SSOAuthenticationMethod, () => Promise<boolean>> = {
        [SSOAuthenticationMethod.MICROSOFT_ENTRA_ID]: () => this.fetchEntraStatus(),
    }

    /**
     * These are preflight checks to validate how is Connectware running
     * They assist the service implementation mappers to drop certain capabilities in case the system can not
     * provide them
     */
    protected readonly capabilitiesCheckers = {
        orchestrator: executeOnce((token: string) =>
            this.request({
                capability: null,
                method: 'GET',
                path: '/api/containers/orchestrator',
                authenticate: token,
                handlers: {
                    200: (response) => response.getJson<OrchestratorResponse>().then(({ orchestrator }) => orchestrator),
                    400: ignoreOrchestratorError,
                    403: ignoreOrchestratorError,
                    503: ignoreOrchestratorError,
                },
            })
        ),
        mfa: executeOnce((token: string) =>
            this.request({
                capability: null,
                method: 'GET',
                path: '/api/auth/mfa',
                authenticate: token,
                handlers: {
                    /**
                     * It should return a boolean because
                     * 'unavailableMfaConfiguration' in src/infrastructure/Connectware/Capabilities.ts
                     * handles only two cases (true/false).
                     * If, for some reason, 'enabled' returns something other than a Boolean (e.g., null or undefined),
                     * then MFA could mistakenly be activated.
                     * */
                    200: (response) => response.getJson<BackendJsonResponseContent<'/api/auth/mfa'>>().then(({ enabled }) => Boolean(enabled)),
                    403: ignoreMfaError,
                    503: ignoreMfaError,
                },
            })
        ),
    }

    constructor (...[options]: ConstructorParameters<typeof ConnectwareHTTPCapabilitiesService>) {
        super(options)

        this.baseURL = options.baseURL
    }

    private createAuthenticationCapabilityCheckers (token: string): CapabilityCheckers {
        return { orchestrator: () => this.capabilitiesCheckers.orchestrator(token), isMfaActive: () => this.capabilitiesCheckers.mfa(token) }
    }

    private mfaLogin ({ secret, username, ...request }: AuthenticationWithOtpRequest | AuthenticationWithBackupcode): Promise<LoginResponse> {
        return this.request<'/api/mfa/login', BackendJsonRequestContent<'/api/mfa/login', 'post'>, LoginResponse>({
            capability: Capability.USE_MFA,
            method: 'POST',
            path: '/api/mfa/login',
            body: { secret, username, ...('backupCode' in request ? mapBackupCodeBodyRequest(request.backupCode) : mapOtpBodyRequest(request.otp)) },
            authenticate: false,
            handlers: {
                200: (response) => response.getJson<LoginResponse>(),
                400: () => new ConnectwareError(ConnectwareErrorType.AUTHENTICATION, 'There were issues while authenticating the user'),
                401: async (response) => {
                    const { banDurationMinutes, triesLeft, error } = await response.getJson<InvalidOTPResponse>()
                    if (error === 'invalid') {
                        return new TokenExpiredError()
                    }

                    if (error === 'wrong OTP') {
                        return new InvalidOtpError({ minutesUntilRetry: banDurationMinutes, triesLeft })
                    }

                    return new ConnectwareError(ConnectwareErrorType.AUTHENTICATION, 'Invalid backupCode')
                },
                403: async (response) => {
                    const { minutesUntilBanEnd } = await response.getJson<UserBannedResponse>()
                    return new InvalidOtpError({ minutesUntilRetry: minutesUntilBanEnd, triesLeft: null })
                },
                500: () => new ConnectwareError(ConnectwareErrorType.SERVER_ERROR, '500 Internal Server Error'),
            },
        })
    }

    private login (request: AuthenticationRequest): Promise<LoginResponse> {
        return this.request<'/api/login', BackendJsonRequestContent<'/api/login', 'post'>, LoginResponse>({
            capability: null,
            method: 'POST',
            path: '/api/login',
            body: request,
            authenticate: false,
            handlers: {
                200: async (response) => response.getJson<LoginResponse>(),
                401: () => new ConnectwareError(ConnectwareErrorType.AUTHENTICATION, 'There were issues while authenticating the user'),
                403: () => new ConnectwareError(ConnectwareErrorType.AUTHENTICATION, 'There were issues while authenticating the user'),
            },
        })
    }

    private fetchBackendSession (token: string): Promise<BackendSessionResponse> {
        return this.request({
            capability: null,
            method: 'GET',
            path: '/api/session',
            authenticate: token,
            handlers: {
                200: (response) => response.getJson<BackendSessionResponse>(),
                401: () => new SessionFromTokenNotFoundError(),
                403: () => new SessionFromTokenNotFoundError(),
            },
        })
    }

    async authenticate (request: AuthenticationRequest | AuthenticationWithOtpRequest | AuthenticationWithBackupcode): Promise<AuthenticationResponse> {
        const backendResponse = await ('secret' in request ? this.mfaLogin(request) : this.login(request))

        if ('needsMfa' in backendResponse) {
            throw new MfaRequiredError(backendResponse.secret)
        }

        const loginResponse = await mapLoginResponse(backendResponse, this.createAuthenticationCapabilityCheckers(backendResponse.token))

        if ('enforceMFAEnrollment' in backendResponse) {
            throw new MfaSetupRequiredError(loginResponse)
        }

        return loginResponse
    }

    fetchSession (token: string): Promise<SessionResponse> {
        return this.fetchBackendSession(token).then((response) => mapSessionResponse(response, this.createAuthenticationCapabilityCheckers(token)))
    }

    fetchAuthenticationStatus (token: string): Promise<AuthenticationStatus> {
        return this.fetchBackendSession(token).then(({ mfa: { enabled, enforced } }) => ({ isMfaEnabled: enabled, isMfaRequired: enforced }))
    }

    async fetchActiveSSOAuthenticationMethods (): Promise<SSOAuthenticationMethod[]> {
        const enabledSSOTypes = await Promise.all(
            objectEntries(this.ssoFetchers).map(([type, fetchIsEnabled]) => fetchIsEnabled().then((enabled) => (enabled ? [type] : [])))
        )
        return enabledSSOTypes.flat()
    }

    fetchSSOAuthenticationUrl (type: SSOAuthenticationMethod): Promise<URL> {
        return Promise.resolve(new URL(this.ssoUrlLoginPaths[type], this.baseURL))
    }

    fetchSubscribableTopics (): Promise<string[]> {
        return this.request({
            capability: Capability.MINIMUM,
            method: 'GET',
            path: '/api/permissions',
            authenticate: true,
            handlers: {
                200: async (response) => {
                    const data = await response.getJson<Permission[]>()
                    return data.reduce<string[]>((reduced, { context, resource }) => (context === 'mqtt' ? [...reduced, resource] : reduced), [])
                },
            },
        })
    }

    flushAuthentication (token: string): Promise<void> {
        return this.request<'/api/logout', BackendJsonRequestContent<'/api/logout', 'post'>, void>({
            capability: null,
            method: 'POST',
            path: '/api/logout',
            body: { token },
            handlers: { 200: () => Promise.resolve() },
        })
    }
}
